import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@utils/classNames';

interface SidebarPopoverPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onPanelMouseEnter?: () => void;
  onPanelMouseLeave?: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  ariaLabel: string;
  children: React.ReactNode;
  className?: string;
}

const PANEL_WIDTH = 260;
const GAP = 8;

/** Popover via portal: supports hover preview + click pin (controlled by parent). */
export const SidebarPopoverPanel: React.FC<SidebarPopoverPanelProps> = ({
  isOpen,
  onClose,
  onPanelMouseEnter,
  onPanelMouseLeave,
  triggerRef,
  ariaLabel,
  children,
  className,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, bridgeTop: 0, bridgeHeight: 0, bridgeLeft: 0 });

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 200;
    let top = rect.top + rect.height / 2 - panelHeight / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - panelHeight - 8));
    setPosition({
      left: rect.right + GAP,
      top,
      bridgeLeft: rect.right,
      bridgeTop: rect.top,
      bridgeHeight: rect.height,
    });
  }, [triggerRef]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onLayout = () => updatePosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, children, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const hoverHandlers = {
    onMouseEnter: onPanelMouseEnter,
    onMouseLeave: onPanelMouseLeave,
  };

  return createPortal(
    <>
      {/* Invisible bridge so hover works across the gap between rail and panel */}
      <div
        className="hidden md:block fixed z-[129]"
        style={{
          left: position.bridgeLeft,
          top: position.bridgeTop,
          width: GAP,
          height: position.bridgeHeight,
        }}
        aria-hidden
        {...hoverHandlers}
      />
      <div
        ref={panelRef}
        role="region"
        aria-label={ariaLabel}
        className={cn(
          'hidden md:block fixed z-[130] w-[260px]',
          'rounded-xl bg-dark-tertiary/98 backdrop-blur-md border border-white/[0.08] shadow-xl',
          'transition-opacity duration-150',
          className
        )}
        style={{
          left: position.left,
          top: position.top,
          width: PANEL_WIDTH,
        }}
        {...hoverHandlers}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body
  );
};
